// The single requestAnimationFrame loop. Reads the active playback source's time and pushes the
// current chord / measure / beat to React only when they change (never per-frame React state).
import type { Controllers } from "../state/controllers";
import { useProjectStore } from "../state/projectStore";
import { usePlaybackStore } from "../state/playbackStore";

export class CursorLoop {
  private started = false;
  private raf = 0;
  private lastChord = "";
  private lastPlaying = false;
  private lastMeasure = -1;
  private lastBeatTime = -1;

  constructor(private c: Controllers) {}

  start(): void {
    if (this.started) return;
    this.started = true;
    const tick = () => {
      this.frame();
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  stop(): void {
    this.started = false;
    cancelAnimationFrame(this.raf);
  }

  private frame(): void {
    const proj = useProjectStore.getState();
    const pb = usePlaybackStore.getState();
    const analysis = proj.analysis;
    const measures = proj.chartMeasures;
    if (!analysis) return;

    const src = this.c.activeSource();
    const playing = src.isPlaying();
    const t = src.getPlaybackTime();

    // current chord label (push to React only when it changes)
    const segs = analysis.chords || [];
    let chord = "—";
    for (let i = 0; i < segs.length; i++) {
      if (t >= segs[i].start && t < segs[i].end) { chord = segs[i].chord; break; }
    }
    if (chord !== this.lastChord) { this.lastChord = chord; pb.setCurrentChord(chord); }

    // current measure (chord grid + PDF score) — push to React only when it changes
    let measure = -1;
    for (let i = 0; i < measures.length; i++) { if (t >= measures[i].startTime - 0.02) measure = i; else break; }
    if (measure !== this.lastMeasure) { this.lastMeasure = measure; pb.setCurrentMeasure(measure); }

    // start time of the beat under the cursor — lets the grid highlight the exact block
    // (so a measure with the same chord twice, e.g. "G Gmaj7 G", doesn't light both G's)
    let beatTime = -1;
    const cm = measures[measure];
    if (cm) for (const b of cm.beats) { if (t >= b.time - 0.02) beatTime = b.time; else break; }
    if (beatTime !== this.lastBeatTime) { this.lastBeatTime = beatTime; pb.setCurrentBeatTime(beatTime); }

    // file source: stop cleanly at the end of the buffer
    if (playing && src === this.c.song) {
      const dur = this.c.song.duration;
      if (dur && t >= dur - 0.05) { this.c.song.pause(); this.c.song.setBaseTime(0); }
    }

    // transport state reflects the ACTIVE source (file, YouTube or metronome) — so playing/pausing
    // the YouTube video via its own controls keeps the play button + cursor in sync.
    if (playing !== this.lastPlaying) { this.lastPlaying = playing; pb.setIsPlaying(playing); }
  }
}
